// System Defaults — the curated view over the platform runtime-config store
// (`/api/super/settings/`, backed by the `SystemSetting` table).
//
// The raw key/value CRUD already lives in `api/Settings`; this module composes
// those calls into the three operations the System Default screen actually
// needs and adds nothing new to the transport layer:
//
//   * read every row in one shot (the catalogue is ~20 keys, not a feed)
//   * upsert by key, because a key with no row is a *valid* state meaning
//     "using the code default" — the screen cannot know up front whether a
//     save is a POST or a PATCH
//   * delete, which on this surface means "revert to the code default"
//
// See BACKEND_CHANGES_ADMINPANEL.md §1 / §1.1.

import { getSettings, createSetting, updateSetting, deleteSetting, invalidateSettingCache } from 'api/Settings';
import { unwrap } from 'utils/notify';

// The store is small, but the endpoint is paginated — walk it rather than
// assuming a single page holds everything. The cap is a runaway guard, not a
// business limit.
const MAX_PAGES = 20;
const PAGE_SIZE = 100;

export const getAllSystemDefaults = async (signal) => {
  const rows = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const body = unwrap(await getSettings({ page, page_size: PAGE_SIZE }, signal));
    const batch = Array.isArray(body) ? body : body?.results || [];
    rows.push(...batch);
    // A bare array response is unpaginated; a short page is the last page.
    if (Array.isArray(body) || batch.length < PAGE_SIZE || !body?.next) break;
  }
  return rows;
};

// `exists` tells us which verb to use. When the caller doesn't know, we try the
// PATCH and fall back to a create on 404 — the row may have been deleted by
// another operator between the page load and the save.
export const upsertSystemDefault = async ({ key, value, description, exists }) => {
  if (exists === false) {
    return createSetting({ key, value, description });
  }
  try {
    return await updateSetting({ key, value, description });
  } catch (err) {
    if (err?.response?.status === 404) {
      return createSetting({ key, value, description });
    }
    throw err;
  }
};

// Deleting the row is how a key goes back to its hardcoded code default —
// there is no "null it out" write.
export const resetSystemDefault = (key) => deleteSetting(key);

export const invalidateSystemDefaultCache = (key) => invalidateSettingCache(key);
